import {
  BACKGROUND_PRESETS,
  describeBackgroundEffect,
  type BackgroundEffect,
  type BackgroundProblem,
} from './liveKitVideoBackground'
import { peekBackgroundName } from './backgroundLibrary'

/**
 * The words for a background problem (SC-158), in the places it is told: the
 * in-game notification, the camera button's warning tooltip, and the Join
 * screen's inline notice and preview cover.
 *
 * Every sentence names only what the surface showing it lets the user do. On
 * the Join screen "pick another background" is the Pick another button or the
 * picker beside it; in game it is the background menu next to the camera
 * button, since the warning's own menu does not offer it. Blur is only ever
 * suggested where there is a "Use blur" to press, and never for blur itself.
 *
 * Its own module because naming an uploaded image needs the library's name
 * cache, and `liveKitVideoBackground` deliberately keeps the upload and session
 * stack out of its imports.
 */

/** Where the words are shown; see the module comment for what each offers. */
export type BackgroundProblemSurface = 'join' | 'game'

/**
 * The user's own name for the image where there is one: they recognise
 * "Beach house", not "Your image". `null` for effects that are not a picture,
 * and for an uploaded image whose name is not known — after a reload, a
 * deleted one cannot be listed to learn it.
 */
export function backgroundEffectName(effect: BackgroundEffect): string | null {
  switch (effect.kind) {
    case 'preset':
      return BACKGROUND_PRESETS.find((preset) => preset.id === effect.id)?.label ?? null
    case 'workspace':
    case 'custom':
      return peekBackgroundName(effect.backgroundId)
    default:
      return null
  }
}

/**
 * `"Beach house"`, or what the effect is when it has no name: `your uploaded
 * background`, `Blur (light)`. Blur is not the picker's "Blur - light" here:
 * set inside a sentence, its dash read as a clause break ("Still using blur -
 * light - retry").
 */
function named(effect: BackgroundEffect): string {
  const name = backgroundEffectName(effect)
  if (name) return `"${name}"`
  if (effect.kind === 'custom') return 'your uploaded background'
  if (effect.kind === 'workspace') return 'your company background'
  if (effect.kind === 'blur') return `Blur (${effect.intensity === 'strong' ? 'strong' : 'light'})`
  return describeBackgroundEffect(effect).toLowerCase()
}

/** `your background "Beach house"` — or, unnamed, the same as {@link named}. */
function yourBackground(effect: BackgroundEffect): string {
  return backgroundEffectName(effect) ? `your background ${named(effect)}` : named(effect)
}

function capitalised(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function pickAnother(surface: BackgroundProblemSurface): string {
  return surface === 'game' ? 'pick another from the background menu' : 'pick another background'
}

/** The full sentence for a notification or the Join screen. */
export function backgroundProblemMessage(
  problem: BackgroundProblem,
  surface: BackgroundProblemSurface = 'join'
): string {
  const { effect } = problem
  switch (problem.cause) {
    case 'gone':
      return `${capitalised(yourBackground(effect))} is no longer available. It may have been deleted. Your camera stays off until you ${pickAnother(surface)} or turn the effect off.`
    case 'unavailable':
      return `We couldn’t load ${yourBackground(effect)}, so your camera stays off. Check your connection and retry, ${pickAnother(surface)}, or turn the effect off.`
    case 'processor-failed':
      return `Your camera background couldn’t start, so your camera stays off. Retry, ${pickAnother(surface)}, or turn the effect off.`
    case 'unsupported':
      return 'This browser or device can’t apply camera backgrounds, so your camera stays off while a background is selected. You can turn on your camera without a background on this device.'
    case 'slow':
      return effect.kind === 'blur'
        ? 'Your device is struggling to apply blur, so your video may be choppy. Turn the effect off for smoother video.'
        : 'Your device is struggling to apply this background, so your video may be choppy. Use blur instead, or turn the effect off.'
    case 'switch-failed':
      return `Couldn’t load ${named(effect)}. You’re still using ${named(problem.stillUsing ?? effect)}. Retry, or ${pickAnother(surface)}.`
  }
}

/** The short version, for the camera button's warning icon and the preview cover. */
export function backgroundProblemTooltip(
  problem: BackgroundProblem,
  surface: BackgroundProblemSurface = 'game'
): string {
  const { effect } = problem
  switch (problem.cause) {
    case 'gone':
      return `${capitalised(yourBackground(effect))} is no longer available. Your camera stays off - ${pickAnother(surface)} or turn the effect off.`
    case 'unavailable':
      return `Couldn’t load ${yourBackground(effect)}. Your camera stays off - retry, ${pickAnother(surface)}, or turn the effect off.`
    case 'processor-failed':
      return `Camera background couldn’t start. Your camera stays off - retry, ${pickAnother(surface)}, or turn the effect off.`
    case 'unsupported':
      return 'This device can’t apply camera backgrounds. Your camera stays off - you can turn it on without a background here.'
    case 'slow':
      return effect.kind === 'blur'
        ? 'Your device is struggling with blur. Turn the effect off for smoother video.'
        : 'Your device is struggling with this background. Use blur instead, or turn the effect off.'
    case 'switch-failed':
      return `Couldn’t load ${named(effect)}. Still using ${named(problem.stillUsing ?? effect)}. Retry, or ${pickAnother(surface)}.`
  }
}

export type BackgroundProblemAction =
  'retry' | 'pick-another' | 'use-blur' | 'turn-off' | 'camera-without-background'

/**
 * What the user can do about `problem`, most useful first. Only what can help:
 * retrying a deleted image fails the same way, on a device that cannot run
 * effects no other background would either, and blur is no way out of blur.
 *
 * In game there is no "Pick another" here: the background menu beside the
 * camera button is where that happens, and the words say so.
 */
export function backgroundProblemActions(
  problem: BackgroundProblem,
  surface: BackgroundProblemSurface = 'join'
): BackgroundProblemAction[] {
  const actions = ((): BackgroundProblemAction[] => {
    switch (problem.cause) {
      case 'gone':
        return ['pick-another', 'turn-off']
      case 'unavailable':
      case 'processor-failed':
        return ['retry', 'pick-another', 'turn-off']
      case 'unsupported':
        return ['camera-without-background']
      case 'slow':
        return problem.effect.kind === 'blur' ? ['turn-off'] : ['use-blur', 'turn-off']
      case 'switch-failed':
        return ['retry', 'pick-another']
    }
  })()
  return surface === 'game' ? actions.filter((action) => action !== 'pick-another') : actions
}

export const BACKGROUND_PROBLEM_ACTION_LABELS: Record<BackgroundProblemAction, string> = {
  retry: 'Retry',
  'pick-another': 'Pick another',
  'use-blur': 'Use blur',
  'turn-off': 'Turn effect off',
  'camera-without-background': 'Turn on camera without background',
}
